<template>
  <div>
    <!--Date - Next Task-->
    <p
      v-if="isNextTask"
      :class="['next_task', {'past_due': isPastDue}]"
    >{{ isPastDue ? "TASK WAS DUE" : "NEXT TASK DUE" }} <strong>{{ dueAtDate }} @ {{ dueAtTime }}</strong></p>

    <div class="task_item flex">
      <!--Checkbox - All Tasks-->
      <div class="task_item--side_column">
        <form-field-checkbox
          class="mb-0"
          :value="task.status"
          :watch-value="task.id"
          :checked-value="ENUMS.TaskStatusEnum.Complete.value"
          :un-checked-value="ENUMS.TaskStatusEnum.Incomplete.value"
          :indeterminate-value="ENUMS.TaskStatusEnum.Ignored.value"
          :disabled="disableTask"
          @input="toggleTask"
        ></form-field-checkbox>
      </div>

      <div class="flex flex-grow">
        <div class="flex-grow">
          <!-- Label - All Tasks -->
          <h3
            :class="['task_item--title', {'line-through': checked, 'disabled': disableTask}]"
            @click="openTaskModal(task)"
          >{{ task.label }}</h3>

          <!-- Due - Other Tasks-->
          <p
            v-if="!isNextTask"
            :class="['task_item--due', {'past_due': isPastDue, 'due_today': isDueToday}]"
          >
            {{ dueLabel }}
            <span
              v-if="task.is_required_to_change_status"
              class="task_item--required"
            >(Required for status change)</span>
          </p>

          <!--Link - All Tasks-->
          <a
            v-if="task.link"
            :class="['task_item--link', {'is_next_task': isNextTask}]"
            :href="task.link"
            target="_blank"
          >{{ task.link }}</a>

          <!--Tags - All Tasks-->
          <tag-list
            v-if="task.tags.length"
            :tags="task.tags"
            class="task_item--list"
          />

          <!-- Description - Other Tasks -->
          <p
            v-if="task.description && !isNextTask"
            class="task_item--description"
          >{{ task.description }}</p>
        </div>

        <!--User Avatar - All Tasks-->
        <div class="task_item--avatar">
          <app-avatar
            v-if="task.assigned_to_id"
            :user-id="task.assigned_to_id"
            :size="24"
          ></app-avatar>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {ENUMS} from "../../../services/enums";
import api from "../../../api";
import utils from "../../../services/utils";
import dates from "../../../services/dates";

export default {
  name: "TaskItem",
  props: {
    task: {
      type: Object,
      required: true
    },
    isNextTask: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      debounceToggleTask: undefined,
      disableTask: false,
    };
  },
  computed: {
    checked() {
      return this.task.status !== ENUMS.TaskStatusEnum.Incomplete.value;
    },
    isIndeterminate() {
      return this.task.status === ENUMS.TaskStatusEnum.Ignored.value;
    },
    isPastDue() {
      return dates.isInPast(this.task.due_at);
    },
    isDueToday() {
      return dates.isToday(this.task.due_at);
    },
    dueLabel() {
      let message = "Due " + this.dueAtDate + " @ " + this.dueAtTime;

      if (this.isPastDue) {
        message = "Was due on " + this.dueAtDate + " @ " + this.dueAtTime;
      }

      if (this.isDueToday) {
        message = "Due Today";
      }

      return message;
    },
    dueAtDate() {
      return dates.formatAsDate(this.task.due_at);
    },
    dueAtTime() {
      return dates.formatAsTime(this.task.due_at);
    },
  },
  methods: {
    toggleTask(status) {
      this.debounceToggleTask = utils.debounce(() => {
        this.disableTask = true;

        api.tasks.updateStatus(this.task.id, {status: status})
          .finally(() => {
            this.disableTask = false;
          });
      }, this.debounceToggleTask, 300);
    },
    openTaskModal(task) {
      if(this.disableTask) return;

      this.$emit('edit', task);
    },
  }
};
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.next_task {
  margin: 8px 0;
  color: $text-muted;
}

.task_item {
  margin-bottom: 1rem;

  &--title {
    margin-bottom: 0.5rem;
    color: $secondary;
    font-size: 1.125rem;
    line-height: 1.2;
    cursor: pointer;
    word-break: break-word;

    &:hover {
      color: $primary;
    }

    &.disabled {
      color: $text-light;
      cursor: default;
    }
  }

  &--side_column {
    width: 24px;
    padding-top: 3px;
  }

  &--avatar {
    width: 30px;
  }

  &--due {
    font-size: 0.75rem;

    &.due_today {
      font-weight: bold;
    }

    &.past_due {
      color: $red;
    }
  }

  &--required {
    color: $red;
  }

  &--list {
    margin-top: 4px;
    margin-bottom: 0;
  }

  &--description {
    margin-top: 6px;
    color: $text-muted;
    font-style: italic;
  }

  &--link {
    display: block;
    margin-top: 2px;
    font-size: 0.75rem;

    &.is_next_task {
      margin-top: -6px;
    }
  }

  ::v-deep .line-through {
    text-decoration: line-through;
  }
}
</style>
